import { useMutation, useQuery } from "@tanstack/react-query";
import { APIs } from "../api/APIs";
import { api } from "../api";
import { useContext } from "react";
import { SiteContext } from "../context/SiteProvider";

export const useBoardItem = () => {
  const { boards, setBoards, setBoardInfo } = useContext(SiteContext);

  const useGetBoards = (keys, select) => {
    const api_info = APIs.board.list();
    return useQuery({
      queryKey: ["/board", keys],
      queryFn: async () => {
        const response = await api(api_info);
        if (response.code === "SUCCESS" && response?.data?.content?.length) {
          let result = {};
          response.data.content.forEach((board) => {
            result[board.boardCode] = board;
          });
          setBoards(result);
        }
        return response;
      },
      refetchOnWindowFocus: false,
      select,
      staleTime: 60 * 5,
    });
  };

  const useGetBoard = (keys, select) => {
    const { boardCode } = keys;

    const api_info = APIs.board.detail(boardCode);
    return useQuery({
      queryKey: ["/board/{boardCode}", keys],
      queryFn: async () => {
        const response = await api(api_info);
        if (response.code === "SUCCESS") {
          setBoardInfo(response.data.boardCode, response.data);
        }
        return response;
      },
      refetchOnWindowFocus: false,
      select,
      enabled: !!boardCode,
      staleTime: 60 * 5,
    });
  };

  const useGetBoardItems = (keys, select) => {
    const {
      boardCode,
      page,
      size,
      sort,
      userId,
      boardCodeIn,
      enabled = true,
    } = keys;

    const query = { page, size, sort };

    if (boardCode) query["boardCode"] = boardCode;
    if (boardCodeIn) query["boardCode_in"] = boardCodeIn;

    if (userId) query.userId = userId;

    const queryStr = new URLSearchParams(query).toString();

    const api_info = APIs.board_item.list(queryStr);

    return useQuery({
      queryKey: ["/board-item?boardCode={boardCode}", keys],
      queryFn: async () => await api(api_info),
      refetchOnWindowFocus: false,
      select,
      enabled: (!!boardCode || !!boardCodeIn) && enabled,
    });
  };

  const useGetBoardItem = (keys, select) => {
    const {
      boardCode,
      itemNo,
      includeAdjacent,
      privateViewToken = "",
      enabled = true,
    } = keys;

    let queryStr = "";

    if (includeAdjacent) queryStr = "?includeAdjacent=true";

    const api_info = APIs.board_item.detail(boardCode, itemNo, queryStr);
    const headers = { ...api_info.headers };
    if (privateViewToken) headers["X-Post-Secret-Token"] = privateViewToken;

    return useQuery({
      queryKey: ["/board-item/{boardCode}/{itemNo}", keys],
      queryFn: async () => await api({ ...api_info, headers }),
      refetchOnWindowFocus: false,
      select,
      enabled: !!itemNo && !!boardCode && enabled,
    });
  };

  const useDownloadBoardItems = (keys, select) => {
    const { boardCode } = keys;

    const api_info = APIs.board_item.download_as_excel(boardCode);
    return useQuery({
      queryKey: ["/board-item/download-data/{boardCode}", keys],
      queryFn: async () => await api(api_info),
      refetchOnWindowFocus: false,
      select,
      enabled: !!boardCode,
    });
  };

  const searchBoardItemsMutation = useMutation({
    mutationFn: async (keys) => {
      const { page, size, boardCode, sort, keySearch, keyword } = keys;

      const query = { page, size, boardCode, sort };

      const queryStr = new URLSearchParams(query).toString();

      let data = {
        combinder: "and",
        criteriaList: [
          {
            key: "boardCode",
            operator: "eq",
            value: [boardCode],
          },
        ],
      };
      if (keySearch?.length && keyword) {
        data.predicateList = [
          {
            combinder: "or",
            criteriaList: keySearch.map((key) => {
              return {
                key,
                operator: "contains",
                value: [keyword],
              };
            }),
          },
        ];
      }

      const api_info = APIs.board_item.search(queryStr, data);
      const response = await api(api_info);
      return response.data;
    },
  });

  const createBoardItemMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, payload } = keys;

      const api_info = APIs.board_item.create(boardCode, payload);
      const response = await api(api_info);
      return response.data;
    },
  });

  const updateBoardItemMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, itemNo, payload, privateViewToken } = keys;

      const api_info = APIs.board_item.update(boardCode, itemNo, payload);
      const headers = { ...api_info.headers };
      if (privateViewToken) headers["X-Post-Secret-Token"] = privateViewToken;

      const response = await api({ ...api_info, headers });
      return response.data;
    },
  });

  const deleteBoardItemMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, itemNo, privateViewToken } = keys;

      const api_info = APIs.board_item.delete(boardCode, itemNo);
      const headers = { ...api_info.headers };
      if (privateViewToken) headers["X-Post-Secret-Token"] = privateViewToken;

      const response = await api({ ...api_info, headers });
      return response.data;
    },
  });

  const verifyPasswordViewBoardMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, itemNo, payload } = keys;

      const api_info = APIs.board_item.verify_password_view(
        boardCode,
        itemNo,
        payload
      );
      const response = await api(api_info);
      return response.data;
    },
  });

  const verifyPasswordEditBoardMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, itemNo, payload } = keys;

      const api_info = APIs.board_item.verify_password_edit(
        boardCode,
        itemNo,
        payload
      );
      const response = await api(api_info);
      return response.data;
    },
  });

  return {
    boards,
    useGetBoards,
    useGetBoard,
    useGetBoardItems,
    useGetBoardItem,
    useDownloadBoardItems,
    searchBoardItemsMutation,
    createBoardItemMutation,
    updateBoardItemMutation,
    deleteBoardItemMutation,
    verifyPasswordViewBoardMutation,
    verifyPasswordEditBoardMutation,
  };
};
